import React, { useState, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import { apiFetchJson, appFetchBlob } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";

// --- CONSTANTES ---
const ESTADOS_PERMITIDOS_IDS = [14, 15];
const TIPO_ESTADO_PIN_ID = 5;

const FORMATO_OPCIONES = [
  { id: "EXCEL", label: "EXCEL" },
  { id: "PDF", label: "PDF" },
];

export default function ReportePinesDeClientes() {
  const [listaEstados, setListaEstados] = useState([]);
  const [loadingEstados, setLoadingEstados] = useState(false);
  const [descargando, setDescargando] = useState(false);

  // --- ESTADOS DEL FORMULARIO ---
  const [cliente, setCliente] = useState(null);
  const [estadoId, setEstadoId] = useState("");
  const [responsable, setResponsable] = useState("");
  const [formato, setFormato] = useState("EXCEL");

  // --- 1. CARGA DE ESTADOS ---
  useEffect(() => {
    const cargarEstados = async () => {
      setLoadingEstados(true);
      try {
        const url = `${apiRoutes.estado.autocomplete()}?tipoId=${TIPO_ESTADO_PIN_ID}`;
        const data = await apiFetchJson(url);

        const filtrados = (data || []).filter((item) =>
          ESTADOS_PERMITIDOS_IDS.includes(item.id),
        );

        setListaEstados(filtrados);
      } catch (error) {
        console.error("Error cargando estados:", error);
      } finally {
        setLoadingEstados(false);
      }
    };

    cargarEstados();
  }, []);

  // --- HANDLER CONSULTAR (DESCARGA EXCEL O PDF) ---
  const handleConsultar = async () => {
    if (!formato) {
      alert("Seleccione un formato");
      return;
    }

    setDescargando(true);

    try {
      const params = new URLSearchParams();
      params.append("tipo", "CLIENTE");

      if (cliente?.id) params.append("clienteId", cliente.id);
      if (estadoId) params.append("estadoId", estadoId);
      if (responsable) params.append("numeroResponsable", responsable);

      const url =
        formato === "PDF"
          ? apiRoutes.pines.exportarPdf(params)
          : apiRoutes.pines.exportar(params);

      // Usamos appFetchBlob que ya maneja la autenticación y la redirección en 401
      const { blob, filename } = await appFetchBlob(
        url,
        { method: "GET" },
        { showLoading: false }, // El componente ya tiene su propio indicador de carga
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");

      const extension = formato === "PDF" ? "pdf" : "xlsx";
      const fallbackFilename = `Reporte_Pines_${timestamp}.${extension}`;

      a.href = downloadUrl;
      a.download = filename || fallbackFilename; // Usar nombre del servidor si está disponible

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // appFetchBlob lanza un error en respuestas no-OK.
      // El caso 401 es manejado dentro de http.jsx y no debería llegar aquí.
      console.error("Error en descarga:", error);
      if (error.status !== 401) {
        alert(`No se pudo descargar: ${error.message}`);
      }
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6">
      <div className="bg-white border rounded-md shadow-sm">
        <div className="px-4 md:px-6 py-4 border-b">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">
            Listado de Pines de Cliente
          </h2>
        </div>

        <div className="p-4 md:p-6">
          <div className="bg-gray-100 p-4 md:p-8 rounded-md space-y-4 md:space-y-5 max-w-4xl mx-auto">
            {/* CLIENTE */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-40 shrink-0 font-bold text-gray-700">
                Cliente:
              </label>
              <div className="w-full">
                <ClienteAutocomplete
                  value={cliente}
                  onChange={setCliente}
                  placeholder="Buscar cliente (vacío = todos)..."
                />
              </div>
            </div>

            {/* ESTADO */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-40 shrink-0 font-bold text-gray-700">
                Estado:
              </label>
              <div className="w-full">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-200"
                  value={estadoId}
                  onChange={(e) => setEstadoId(e.target.value)}
                  disabled={loadingEstados}
                >
                  <option value="">--- Seleccione ---</option>

                  {loadingEstados ? (
                    <option>Cargando...</option>
                  ) : (
                    <>
                      {listaEstados.map((est) => (
                        <option key={est.id} value={est.id}>
                          {est.label}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* RESPONSABLE */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-40 shrink-0 font-bold text-gray-700">
                Nro. Responsable:
              </label>
              <div className="w-full">
                <input
                  type="text"
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={responsable}
                  onChange={(e) => setResponsable(e.target.value)}
                  placeholder="Opcional"
                />
              </div>
            </div>

            {/* FORMATO */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <label className="md:w-40 shrink-0 font-bold text-gray-700">
                Formato*:
              </label>
              <div className="w-full">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                >
                  {FORMATO_OPCIONES.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-4 border-t bg-gray-50 flex justify-start">
          <button
            onClick={handleConsultar}
            disabled={loadingEstados || descargando}
            className={`flex items-center gap-2 text-white font-semibold py-2 px-6 rounded shadow transition-colors ${
              descargando
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-800 hover:bg-green-900"
            }`}
          >
            {descargando ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            ) : (
              <FaSearch className="text-sm" />
            )}
            <span>{descargando ? "Descargando..." : "Consultar"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
